import { useState, KeyboardEvent } from 'react'

interface TagInputProps {
  tags: string[]
  onChange: (tags: string[]) => void
  label?: string
  example?: string
  hint?: string
}

export function TagInput({ tags, onChange, label, example, hint }: TagInputProps) {
  const [input, setInput] = useState('')

  const commit = () => {
    const val = input.trim().toLowerCase().replace(/\s+/g, '-')
    if (val && !tags.includes(val)) {
      onChange([...tags, val])
    }
    setInput('')
  }

  const handleKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      commit()
    } else if (e.key === 'Backspace' && input === '' && tags.length > 0) {
      onChange(tags.slice(0, -1))
    }
  }

  const removeTag = (tag: string) => onChange(tags.filter((t) => t !== tag))

  return (
    <>
      <div className="field">
        {label && <label className="field-label">{label}</label>}
        {example && <p className="field-example">e.g. {example}</p>}
        <div className="tag-input-wrap">
          {tags.map((tag) => (
            <span key={tag} className="tag-pill">
              {tag}
              <button
                type="button"
                className="tag-remove"
                onClick={() => removeTag(tag)}
                aria-label={`Remove ${tag}`}
              >✕</button>
            </span>
          ))}
          <input
            className="tag-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKey}
            onBlur={commit}
            placeholder={tags.length === 0 ? 'Type a tag and press Enter' : ''}
          />
        </div>
        {hint && <p className="field-hint">{hint}</p>}
      </div>

      <style>{`
        .tag-input-wrap {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          align-items: center;
          padding: 7px 10px;
          background: var(--bg-input);
          border: 1px solid var(--border);
          border-radius: var(--radius-md);
          min-height: 42px;
          cursor: text;
          transition: border-color 0.15s;
        }
        .tag-input-wrap:focus-within { border-color: var(--accent); }
        .tag-pill {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 3px 8px;
          background: var(--accent-subtle);
          color: var(--accent);
          border-radius: 99px;
          font-size: 0.78rem;
          font-weight: 500;
          border: 1px solid rgba(59,130,246,0.3);
        }
        .tag-remove {
          font-size: 0.6rem;
          color: var(--accent);
          opacity: 0.7;
          line-height: 1;
          padding: 1px;
          transition: opacity 0.15s;
        }
        .tag-remove:hover { opacity: 1; }
        .tag-input {
          flex: 1;
          min-width: 100px;
          background: transparent;
          border: none;
          outline: none;
          color: var(--text-primary);
          font-size: 0.875rem;
          font-family: var(--font-sans);
          padding: 0;
        }
        .tag-input::placeholder { color: var(--text-muted); }
      `}</style>
    </>
  )
}
